import * as THREE from 'three';
import type { Field } from '../state/types';

export interface MistParticle {
  mesh: THREE.Mesh<THREE.SphereGeometry, THREE.MeshStandardMaterial>;
  life: number;
  maxLife: number;
  velocity: THREE.Vector3;
}

/** Soil box is height 0.2 centered at y=0.1 → top face at y=0.2. */
export const FIELD_SOIL_TOP_Y = 0.2;

export interface ScannerEffect {
  group: THREE.Group;
  setActive: (active: boolean) => void;
  /**
   * Apex at the drone camera (world), base corners exactly on the field
   * soil platform top corners.
   */
  update: (apexWorld: THREE.Vector3, field: Pick<Field, 'x' | 'z' | 'size'>) => void;
}

export interface EffectsHandles {
  scanner: ScannerEffect;
  mistParticles: MistParticle[];
}

const SCAN_CYAN = 0x22d3ee;
const SCAN_CYAN_DIM = 0x0891b2;
const GRID_DIVS = 4;
const FACE_SAMPLES = 3;
const DOT_COUNT = 14;

function writeVec(arr: Float32Array, i: number, v: THREE.Vector3): number {
  arr[i] = v.x;
  arr[i + 1] = v.y;
  arr[i + 2] = v.z;
  return i + 3;
}

function writeLine(arr: Float32Array, i: number, a: THREE.Vector3, b: THREE.Vector3): number {
  i = writeVec(arr, i, a);
  return writeVec(arr, i, b);
}

function createScanDots(count: number): THREE.Group {
  const group = new THREE.Group();
  group.name = 'scannerDots';

  const geo = new THREE.SphereGeometry(0.04, 6, 6);
  const mat = new THREE.MeshBasicMaterial({
    color: SCAN_CYAN,
    transparent: true,
    opacity: 0.9,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });

  for (let i = 0; i < count; i++) {
    const dot = new THREE.Mesh(geo, mat.clone());
    // Normalized offsets in [-1, 1] — remapped to field each update
    dot.userData.nx = (Math.random() * 2 - 1) * 0.85;
    dot.userData.nz = (Math.random() * 2 - 1) * 0.85;
    dot.userData.phase = Math.random() * Math.PI * 2;
    group.add(dot);
  }

  return group;
}

export function createScannerEffect(): ScannerEffect {
  const group = new THREE.Group();
  group.name = 'scanner';
  group.visible = false;
  group.frustumCulled = false;

  // 4 triangles × 3 verts
  const faceCount = 4 * 3;
  const facePos = new Float32Array(faceCount * 3);
  const faceGeo = new THREE.BufferGeometry();
  faceGeo.setAttribute('position', new THREE.BufferAttribute(facePos, 3));
  const faceMat = new THREE.MeshBasicMaterial({
    color: SCAN_CYAN,
    transparent: true,
    opacity: 0.12,
    depthWrite: false,
    side: THREE.DoubleSide,
    blending: THREE.AdditiveBlending,
  });
  const faces = new THREE.Mesh(faceGeo, faceMat);
  faces.frustumCulled = false;
  faces.renderOrder = 2;

  // 4 apex→corner + 4 base edges = 8 lines × 2 verts
  const edgeCount = 8 * 2;
  const edgePos = new Float32Array(edgeCount * 3);
  const edgeGeo = new THREE.BufferGeometry();
  edgeGeo.setAttribute('position', new THREE.BufferAttribute(edgePos, 3));
  const edgeMat = new THREE.LineBasicMaterial({
    color: SCAN_CYAN,
    transparent: true,
    opacity: 0.95,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  const edges = new THREE.LineSegments(edgeGeo, edgeMat);
  edges.frustumCulled = false;
  edges.renderOrder = 3;

  // Rings: GRID_DIVS × 4 edges × 2 verts + meridians: 4 faces × FACE_SAMPLES × 2 verts
  const gridLineCount = GRID_DIVS * 4 + 4 * FACE_SAMPLES;
  const gridPos = new Float32Array(gridLineCount * 2 * 3);
  const gridGeo = new THREE.BufferGeometry();
  gridGeo.setAttribute('position', new THREE.BufferAttribute(gridPos, 3));
  const gridMat = new THREE.LineBasicMaterial({
    color: SCAN_CYAN_DIM,
    transparent: true,
    opacity: 0.45,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  const grid = new THREE.LineSegments(gridGeo, gridMat);
  grid.frustumCulled = false;
  grid.renderOrder = 3;

  const dots = createScanDots(DOT_COUNT);
  group.add(faces, edges, grid, dots);

  const apex = new THREE.Vector3();
  const c0 = new THREE.Vector3();
  const c1 = new THREE.Vector3();
  const c2 = new THREE.Vector3();
  const c3 = new THREE.Vector3();
  const corners = [c0, c1, c2, c3];
  const tmpA = new THREE.Vector3();
  const tmpB = new THREE.Vector3();

  return {
    group,
    setActive(active: boolean) {
      group.visible = active;
      if (!active) {
        faceMat.opacity = 0;
        edgeMat.opacity = 0;
        gridMat.opacity = 0;
      }
    },
    update(apexWorld: THREE.Vector3, field: Pick<Field, 'x' | 'z' | 'size'>) {
      apex.copy(apexWorld);
      const half = field.size * 0.5;
      const y = FIELD_SOIL_TOP_Y;
      // Field soil top corners (world), matching BoxGeometry top face
      c0.set(field.x + half, y, field.z + half);
      c1.set(field.x + half, y, field.z - half);
      c2.set(field.x - half, y, field.z - half);
      c3.set(field.x - half, y, field.z + half);

      // Faces: apex + each base edge
      let fi = 0;
      fi = writeVec(facePos, fi, apex);
      fi = writeVec(facePos, fi, c0);
      fi = writeVec(facePos, fi, c1);
      fi = writeVec(facePos, fi, apex);
      fi = writeVec(facePos, fi, c1);
      fi = writeVec(facePos, fi, c2);
      fi = writeVec(facePos, fi, apex);
      fi = writeVec(facePos, fi, c2);
      fi = writeVec(facePos, fi, c3);
      fi = writeVec(facePos, fi, apex);
      fi = writeVec(facePos, fi, c3);
      writeVec(facePos, fi, c0);
      faceGeo.attributes.position.needsUpdate = true;
      faceGeo.computeBoundingSphere();

      // Edges
      let ei = 0;
      ei = writeLine(edgePos, ei, apex, c0);
      ei = writeLine(edgePos, ei, apex, c1);
      ei = writeLine(edgePos, ei, apex, c2);
      ei = writeLine(edgePos, ei, apex, c3);
      ei = writeLine(edgePos, ei, c0, c1);
      ei = writeLine(edgePos, ei, c1, c2);
      ei = writeLine(edgePos, ei, c2, c3);
      writeLine(edgePos, ei, c3, c0);
      edgeGeo.attributes.position.needsUpdate = true;
      edgeGeo.computeBoundingSphere();

      // Grid rings + meridians
      let gi = 0;
      for (let r = 1; r <= GRID_DIVS; r++) {
        const t = r / (GRID_DIVS + 1);
        for (let c = 0; c < 4; c++) {
          tmpA.lerpVectors(apex, corners[c], t);
          tmpB.lerpVectors(apex, corners[(c + 1) % 4], t);
          gi = writeLine(gridPos, gi, tmpA, tmpB);
        }
      }
      for (let face = 0; face < 4; face++) {
        const a = corners[face];
        const b = corners[(face + 1) % 4];
        for (let s = 1; s <= FACE_SAMPLES; s++) {
          const u = s / (FACE_SAMPLES + 1);
          tmpA.set(a.x * (1 - u) + b.x * u, a.y * (1 - u) + b.y * u, a.z * (1 - u) + b.z * u);
          gi = writeLine(gridPos, gi, apex, tmpA);
        }
      }
      gridGeo.attributes.position.needsUpdate = true;
      gridGeo.computeBoundingSphere();

      const pulse = 0.5 + 0.5 * Math.sin(Date.now() * 0.008);
      faceMat.opacity = 0.08 + pulse * 0.08;
      edgeMat.opacity = 0.75 + pulse * 0.2;
      gridMat.opacity = 0.3 + pulse * 0.2;

      dots.children.forEach((child, i) => {
        const mesh = child as THREE.Mesh;
        const mat = mesh.material as THREE.MeshBasicMaterial;
        const nx = mesh.userData.nx as number;
        const nz = mesh.userData.nz as number;
        mesh.position.set(field.x + nx * half, y + 0.02, field.z + nz * half);
        const phase = (mesh.userData.phase as number) ?? i;
        const twinkle = 0.45 + 0.55 * Math.sin(Date.now() * 0.012 + phase);
        mat.opacity = twinkle;
        mesh.scale.setScalar(0.7 + twinkle * 0.6);
      });
    },
  };
}

export function createEffects(scene: THREE.Scene): EffectsHandles {
  const scanner = createScannerEffect();
  scene.add(scanner.group);

  const mistParticles: MistParticle[] = [];
  const maxMistParticles = 30;
  const mistGroup = new THREE.Group();
  scene.add(mistGroup);

  const mistGeo = new THREE.SphereGeometry(0.12, 4, 4);
  const mistMat = new THREE.MeshStandardMaterial({
    color: 0x34d399,
    emissive: 0x34d399,
    emissiveIntensity: 0.8,
    transparent: true,
    opacity: 0,
    depthWrite: false,
  });

  for (let i = 0; i < maxMistParticles; i++) {
    const part = new THREE.Mesh(mistGeo, mistMat.clone());
    part.visible = false;
    mistGroup.add(part);
    mistParticles.push({
      mesh: part,
      life: 0,
      maxLife: 1.0,
      velocity: new THREE.Vector3(),
    });
  }

  return { scanner, mistParticles };
}
